import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import posthog from "posthog-js";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { clearAllSessionCaches } from "@/lib/cache-utils";
import { Sentry } from "@/lib/sentry";

// AbortError benin du Navigator Locks de supabase-js (page navigates / autre onglet tient le lock)
const isAbortError = (err: unknown) =>
  err instanceof Error &&
  (err.name === "AbortError" || err.message.includes("signal is aborted"));

/**
 * Identifie l'utilisateur courant dans PostHog pour que ses sessions
 * ne soient plus anonymes. Safe-guard : si PostHog n'est pas initialise
 * (ex: env vars absentes), la fonction ne fait rien.
 */
function identifyPostHogUser(user: User | null | undefined) {
  if (!user || !posthog.__loaded) return;
  const meta = (user.user_metadata ?? {}) as Record<string, unknown>;
  posthog.identify(user.id, {
    email: user.email,
    full_name: meta.full_name as string | undefined,
    first_name: meta.first_name as string | undefined,
    last_name: meta.last_name as string | undefined,
    city: meta.city as string | undefined,
  });
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signUp: (email: string, password: string, firstName: string, lastName: string, city: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const previousUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Si le refresh token a echoue, deconnecter proprement
      if (event === "TOKEN_REFRESHED" && !session) {
        clearAllSessionCaches();
        supabase.auth.signOut().catch((err) => {
          if (!isAbortError(err)) Sentry.captureException(err);
        });
        return;
      }

      if (event === "SIGNED_OUT") {
        clearAllSessionCaches();
        previousUserIdRef.current = null;
        // Reset PostHog pour ne pas melanger les sessions si un autre user se connecte
        if (posthog.__loaded) posthog.reset();
        setSession(null);
        setUser(null);
        setLoading(false);
        return;
      }

      // Detecter un changement d'utilisateur (nouveau signIn avec un user different)
      if (event === "SIGNED_IN" && session?.user) {
        const newUserId = session.user.id;
        if (previousUserIdRef.current && previousUserIdRef.current !== newUserId) {
          clearAllSessionCaches();
          if (posthog.__loaded) posthog.reset();
        }
        previousUserIdRef.current = newUserId;
        identifyPostHogUser(session.user);
      }

      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        setSession(session);
        setUser(session?.user ?? null);
        previousUserIdRef.current = session?.user?.id ?? null;
        // Identifier l'user au chargement initial si deja connecte
        identifyPostHogUser(session?.user);
        setLoading(false);
      })
      .catch((err) => {
        // onAuthStateChange ci-dessus livrera la session si elle existe ; on debloque juste l'UI
        setLoading(false);
        if (!isAbortError(err)) Sentry.captureException(err);
      });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, firstName: string, lastName: string, city: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
        data: {
          first_name: firstName,
          last_name: lastName,
          city,
          full_name: `${firstName} ${lastName}`.trim(),
        },
      },
    });
    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error as Error };

    // Bloquer si l'email n'est pas confirme
    if (!data.user?.email_confirmed_at) {
      await supabase.auth.signOut();
      return { error: new Error("Veuillez confirmer votre email avant de vous connecter. Verifiez votre boite de reception.") };
    }

    return { error: null };
  };

  const signOut = async () => {
    clearAllSessionCaches();
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ session, user, loading, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
